import { camelCaseObject, snakeCaseObject, getConfig } from '@edx/frontend-platform';
import { getAuthenticatedHttpClient } from '@edx/frontend-platform/auth';

import { LIBRARY_LIST_PAGE_SIZE } from '../constants';

function formatStudioHomeLibrariesV2Params(customParams = {}) {
  const { search, order, language, course } = snakeCaseObject(customParams);
  const filteredParams = {};

  Object.entries({ search, order, language, course }).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      filteredParams[key] = value;
    }
  });

  return {
    ...filteredParams,
    page: 1,
    page_size: LIBRARY_LIST_PAGE_SIZE,
  };
}

export const getApiBaseUrl = () => getConfig().STUDIO_BASE_URL;
export const getStudioHomeApiUrl = () => new URL('api/contentstore/v1/home', getApiBaseUrl()).href;
export const getRequestCourseCreatorUrl = () => new URL('request_course_creator', getApiBaseUrl()).href;
export const getCourseNotificationUrl = (url) => new URL(url, getApiBaseUrl()).href;

/**
 * Get's studio home data.
 * @param {string} search
 * @returns {Promise<Object>}
 */
export async function getStudioHomeData() {
  const { data } = await getAuthenticatedHttpClient().get(getStudioHomeApiUrl());
  return camelCaseObject(data);
}

export async function getStudioHomeCourses(search) {
  const { data } = await getAuthenticatedHttpClient().get(`${getApiBaseUrl()}/api/contentstore/v1/home/courses${search}`);
  return camelCaseObject(data);
}
/**
 * Get's studio home courses.
 * @param {string} search - Query string parameters for filtering the courses.
 * @param {object} customParams - Additional custom parameters for the API request.
 * @returns {Promise<Object>} - A Promise that resolves to the response data containing the studio home courses.
 * Note: We are changing /api/contentstore/v1 to /api/contentstore/v2 due to upcoming breaking changes.
 * Features such as pagination, filtering, and ordering are better handled in the new version.
 * Please refer to this PR for further details: https://github.com/openedx/edx-platform/pull/34173
 */
export async function getStudioHomeCoursesV2(search, customParams) {
  const customParamsFormat = snakeCaseObject(customParams);
  const { data } = await getAuthenticatedHttpClient().get(`${getApiBaseUrl()}/api/contentstore/v2/home/courses${search}`, { params: customParamsFormat });
  return camelCaseObject(data);
}

export async function getStudioHomeLibraries() {
  const { data } = await getAuthenticatedHttpClient().get(`${getApiBaseUrl()}/api/contentstore/v1/home/libraries`);
  return camelCaseObject(data);
}

/**
 * Get studio home libraries (v2 API with filtering support).
 * @param {object} customParams - Query parameters such as course filter.
 * @returns {Promise<Object>}
 */
export async function getStudioHomeLibrariesV2(customParams = {}) {
  const customParamsFormat = formatStudioHomeLibrariesV2Params(customParams);
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiBaseUrl()}/api/contentstore/v2/home/libraries`,
    { params: customParamsFormat },
  );
  return camelCaseObject(data);
}

/**
 * Get released languages for Studio course language filtering.
 * @returns {Promise<Array<{label: string, value: string}>>}
 */
export async function getStudioHomeReleasedLanguages() {
  const { data } = await getAuthenticatedHttpClient().get(
    `${getApiBaseUrl()}/api/contentstore/v2/home/released-languages`,
  );
  const formatted = camelCaseObject(data);
  return formatted.languages ?? [];
}

/**
 * Handle course notification requests.
 * @param {string} url
 * @returns {Promise<Object>}
*/
export async function handleCourseNotification(url) {
  const { data } = await getAuthenticatedHttpClient().delete(getCourseNotificationUrl(url));
  return camelCaseObject(data);
}

/**
 * Send user request to course creation access for studio home data.
 * @returns {Promise<Object>}
 */
export async function sendRequestForCourseCreator() {
  const { data } = await getAuthenticatedHttpClient().post(getRequestCourseCreatorUrl());
  return camelCaseObject(data);
}
